<template>
  <div v-show="isCustomFieldValid">
    <!-- Preview Mode: Use AutomationMultiAssigneeInput -->
    <div v-if="props.isPreview">
      <automation-multi-assignee-input
        title="Select Users"
        placeholder="Select users"
        :value="{
          assigneeIds: model,
          hasNoAssigneeSelected: false,
        }"
        @assignee-ids-updated="handlePreviewAssigneeUpdate"
      />
    </div>

    <!-- Non-Preview Mode: Regular Selectors -->
    <div v-else>
      <label
        v-if="!props.flat && !props.hideLabel && customField?.name"
        class="input-label tw-flex tw-items-center"
        :for="`${customField?.name}`"
      >
        {{ customField?.name }}
        <span
          v-if="props.customField.required"
          class="tw-ml-0.5 tw-text-sm tw-text-red"
        >
          *
        </span>
        <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
          {{ customField.description }}
        </help-text>

        <button
          v-if="model?.length && props.clearable && !props.readonly"
          tabindex="-1"
          class="primary-link tw-ml-1 tw-normal-case"
          @click.stop="emit('value-updated', null)"
          @keyup.enter.stop="emit('value-updated', null)"
        >
          Clear
        </button>
      </label>

      <!-- Multi-user trigger and selector -->
      <div v-if="isMultiSelect">
        <slot name="trigger">
          <empty-select-shell
            :is-open="showMultiAssigneeSelector"
            :error-message="showErrorMessage ? errorMessage : ''"
            :readonly="props.readonly"
            :style="{ paddingLeft: '0.5rem' }"
            @click="!props.readonly && openMultiUserSelector()"
          >
            <span
              class="tw-mr-2 tw-text-xs"
              :class="{ 'tw-py-1': !!model?.length }"
            >
              <span
                v-if="!selectedMultiUsers.length"
                class="tw-select-none tw-text-gray-medium"
              >
                <template v-if="props.readonly">No users selected</template>
                <template v-else>Select users</template>
              </span>
              <span
                v-for="assignee in selectedMultiUsers"
                :key="assignee.id"
                class="tw-mr-1"
              >
                <user-tag-with-avatar
                  :closable="false"
                  :member="assignee"
                  style="top: 3px"
                />
              </span>
            </span>
          </empty-select-shell>
        </slot>

        <multi-user-selector
          ref="multiUserSelectorRef"
          :value="selectedMultiUsers"
          :title="customField.name"
          :primary-color="Colors.BLUE"
          :popup-props="{ offset: [-12, 12] }"
          :disabled="props.readonly"
          is-dialog
          :class="{ 'tw-h-full': props.flat }"
          :user-query="userQuery"
          :no-item-option="true"
          no-item-text="No User"
          :clearable="!props.customField.required"
          :show-archived-toggle="true"
          use-select-button
          @selected="selectAssigneeIds"
          @show="showMultiAssigneeSelector = true"
          @hide="showMultiAssigneeSelector = false"
          @keyup.delete="handleDeleteKey"
        />
      </div>

      <!-- Single-user trigger and selector -->
      <div v-else class="tw-w-full">
        <slot name="trigger">
          <empty-select-shell
            :is-open="showSingleAssigneeSelector"
            :error-message="showErrorMessage ? errorMessage : ''"
            :readonly="props.readonly"
            :style="{ paddingLeft: '0.5rem' }"
            @click="!props.readonly && openSingleUserSelector()"
          >
            <span
              class="tw-mr-2 tw-text-xs"
              :class="{ 'tw-py-1': !!selectedSingleUser }"
            >
              <span v-if="!selectedSingleUser" class="tw-text-gray-medium">
                <template v-if="props.readonly">No user selected</template>
                <template v-else>Select a user</template>
              </span>
              <span v-if="selectedSingleUser" class="tw-mr-1">
                <user-tag-with-avatar
                  :closable="false"
                  :member="selectedSingleUser"
                  style="top: 3px"
                />
              </span>
            </span>
          </empty-select-shell>
        </slot>

        <single-user-selector
          ref="singleUserSelectorRef"
          :value="selectedSingleUser"
          class="tw-w-full"
          :class="{ 'tw-h-full': props.flat }"
          :no-item-option="true"
          no-item-text="No User"
          :title="props.customField.name"
          :popup-props="{ offset: [-12, 12] }"
          :disabled="props.readonly"
          is-dialog
          :user-query="userQuery"
          :clearable="!props.customField.required"
          @selected="updateSingleUser"
          @show="showSingleAssigneeSelector = true"
          @hide="showSingleAssigneeSelector = false"
          @keyup.delete="handleDeleteKey"
        />
      </div>

      <!-- Error message (shared between preview and non-preview) -->
      <span
        v-if="!!errorMessage && showErrorMessage && !props.flat"
        class="form-input__error-message"
      >
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  OrganizationMembership,
  OrganizationMembershipQuery,
} from '@/models/user.models';
import { isEqual } from 'lodash-es';
import { ref, computed, watch } from 'vue';
import EmptySelectShell from '../core/EmptySelectShell.vue';
import UserTagWithAvatar from '../core/UserTagWithAvatar.vue';
import HelpText from '../design-system/misc/HelpText.vue';
import MultiUserSelector from '@/components/modal/selectors/user/MultiUserSelector.vue';
import SingleUserSelector from '@/components/modal/selectors/user/SingleUserSelector.vue';
import AutomationMultiAssigneeInput from '@/components/automation/inputs/AutomationMultiAssigneeInput.vue';
import {
  Colors,
  CustomField,
  CustomFieldType,
  CustomFieldEntitySubType,
} from '@/api';
import { useOrgMembershipsQuery } from '@/utils/users';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string[];
  clearable?: boolean;
  flat?: boolean;
  hideLabel?: boolean;
  readonly?: boolean;
  isPreview?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string[] | null): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const { filteredCachedMembers: orgMembers } = useOrgMembershipsQuery();

const showMultiAssigneeSelector = ref(false);
const showSingleAssigneeSelector = ref(false);
const multiUserSelectorRef = ref<InstanceType<typeof MultiUserSelector>>();
const singleUserSelectorRef = ref<InstanceType<typeof SingleUserSelector>>();

const isMultiSelect = computed(() => {
  return props.customField.isMultiSelect;
});

const userQuery = computed(() => {
  if (!props.customField.filters || !props.customField.filters.userId?.length)
    return undefined;
  return props.customField.filters as OrganizationMembershipQuery;
});

const isCustomFieldValid = computed(() => {
  // In preview mode, allow rendering even if type/subType are undefined
  if (props.isPreview) {
    const hasCorrectType =
      !props.customField.type ||
      props.customField.type === CustomFieldType.ENTITY;
    const hasCorrectSubType =
      !props.customField.subType ||
      props.customField.subType === CustomFieldEntitySubType.USER;
    return hasCorrectType && hasCorrectSubType;
  }

  // In non-preview mode, require exact type/subType match
  if (
    props.customField.type !== CustomFieldType.ENTITY ||
    props.customField.subType !== CustomFieldEntitySubType.USER
  )
    return false;
  return true;
});

const model = ref<string[]>(props.value || []);
watch([() => props.value, isMultiSelect], () => {
  model.value = props.value ?? [];
});

const selectedMultiUsers = computed(() => {
  if (!model.value) return [];
  return orgMembers.value.filter((m) => model.value?.includes(m.userId));
});

const selectedSingleUser = computed(() => {
  if (!model.value) return null;
  return orgMembers.value.find((m) => m.userId === model.value?.[0]);
});

const errorMessage = computed(() => {
  if (!model.value && props.customField.required) {
    return 'This field is required';
  }
  return '';
});

const showErrorMessage = ref(false);

watch(
  model,
  () => {
    showErrorMessage.value = false;
  },
  { deep: true }
);

function openMultiUserSelector() {
  multiUserSelectorRef.value?.showPopup();
}

function openSingleUserSelector() {
  singleUserSelectorRef.value?.showPopup();
}

function selectAssigneeIds(users: OrganizationMembership[]) {
  if (!isMultiSelect.value) return;
  model.value = users.map((u) => u.userId);
  onValueChange();
}

function updateSingleUser(member?: OrganizationMembership) {
  if (isMultiSelect.value) return;
  model.value = member ? [member.userId] : [];
  onValueChange();
}

function onValueChange() {
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (isEqual(props.value, model.value)) return;
  emit('value-updated', model.value);
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

function handleDeleteKey() {
  if (!!model.value && props.clearable) {
    emit('value-updated', null);
  }
}

function handlePreviewAssigneeUpdate(value: {
  assigneeIds: string[];
  hasNoAssigneeSelected: boolean;
}) {
  model.value = value.assigneeIds;
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
